/**
 * Drives a horizontal scroll-snap carousel. Returns a ref to attach to the scroll track, a
 * scrollBy handler that advances by `step` px (positive = next, negative = previous), atStart/atEnd
 * flags so callers can disable the previous/next controls at the track's ends, and hasOverflow so
 * callers can hide the nav entirely when the items fit without scrolling. Flags recompute on scroll,
 * on resize, and whenever `itemCount` changes (e.g. the list is filtered after the market resolves).
 */
import { useEffect, useRef, useState } from 'react'

/**
 * @param {number} step - horizontal distance in px to scroll per scrollBy(direction) call
 * @param {number} itemCount - number of items in the track; re-measures the ends when it changes
 * @returns {{ trackRef: object, scrollBy: (direction: number) => void, atStart: boolean, atEnd: boolean, hasOverflow: boolean }}
 */
export const useCarouselNav = (step, itemCount) => {
  const trackRef = useRef(null)
  const [atStart, setAtStart] = useState(true)
  const [atEnd, setAtEnd] = useState(false)
  const [hasOverflow, setHasOverflow] = useState(false)

  useEffect(() => {
    const track = trackRef.current
    if (!track) {
      return undefined
    }
    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = track
      setAtStart(scrollLeft <= 1)
      setAtEnd(scrollLeft + clientWidth >= scrollWidth - 1)
      setHasOverflow(scrollWidth - clientWidth > 1)
    }
    update()
    track.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    let observer
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(update)
      observer.observe(track)
    }
    return () => {
      track.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
      if (observer) {
        observer.disconnect()
      }
    }
  }, [itemCount])

  const scrollBy = direction => {
    const track = trackRef.current
    if (!track) {
      return
    }
    track.scrollBy({ left: direction * step, behavior: 'smooth' })
  }

  return { trackRef, scrollBy, atStart, atEnd, hasOverflow }
}
